/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable react/display-name */
import { Checkbox, CheckboxProps, Tooltip } from '@material-ui/core';
import React, { useEffect } from 'react';
import { CellProps, HeaderProps, Hooks, Row, UseTableInstanceProps } from 'react-table';
import { withExcelOptions } from '../../DataGridExportMenu/excelExport/ExcelTableColumn';

const pluginName = 'useRowSelector';

/**
 * Custom row selection hook that provides a new react-table column of checkboxes on the left side of the table.  This hook
 * takes in a function that exposes the selected row ids to the consumer.
 * @param {(rowIds: Record<string, boolean>) => void} onRowSelect A function that exposes an object of selected row ids.  The object is a record with keys
 * of row id's (which are a simple numerical index in the table unless a row id setter is provided to the table), and the values will always be true.
 * The object will only contain rows that are selected, and the values will thus never be 'false'.
 * @returns {(hooks: <T>) => void} The standard return for react-table hooks
 */
export function useRowSelector<T extends Record<string, unknown>>(
  onRowSelect: (rowIds: Record<string, boolean>) => void,
  rowWidth = 75,
  additionalStyle: React.CSSProperties = {},
  headerCheckboxProps: CheckboxProps = {},
  disableFunc?: (row: Row<T>, selectedRows?: Row<T>[]) => boolean,
  tooltipParams?: { text: string; displayTooltip: (row: Row<T>, selectedRows?: Row<T>[]) => boolean },
) {
  function useRowSelectorReturn(hooks: Hooks<T>) {
    hooks.useInstance.push((instance: UseTableInstanceProps<T>) => {
      const hasUseRowSelect = !!instance.plugins.find((plugin) => plugin.pluginName === 'useRowSelect');
      if (!hasUseRowSelect) {
        throw new Error('useRowSelector requires useRowSelect!');
      }

      const { state } = instance;

      useEffect(() => {
        onRowSelect(state.selectedRowIds);
      }, [Object.keys(state.selectedRowIds ?? {}).length]);
    });

    hooks.allColumns.push((columns) => [
      {
        id: 'Selection',
        Header: ({ getToggleAllRowsSelectedProps }: HeaderProps<T>) => (
          <Checkbox
            color="primary"
            {...(getToggleAllRowsSelectedProps && getToggleAllRowsSelectedProps())}
            style={{ ...getToggleAllRowsSelectedProps?.()?.style, ...additionalStyle }}
            {...headerCheckboxProps}
          />
        ),
        Cell: ({ row, selectedFlatRows }: CellProps<T>) => {
          const toggleRowSelectedProps = row.getToggleRowSelectedProps?.();
          if (tooltipParams && tooltipParams.displayTooltip(row, selectedFlatRows)) {
            delete toggleRowSelectedProps.title;
          }
          const checkbox =
            disableFunc && disableFunc(row, selectedFlatRows) ? (
              <Checkbox
                inputProps={{
                  disabled: true,
                  style: {
                    ...toggleRowSelectedProps?.style,
                    cursor: 'default',
                  },
                }}
                color="primary"
                onClick={(e) => e.stopPropagation()}
                {...toggleRowSelectedProps}
                style={{
                  ...toggleRowSelectedProps?.style,
                  ...additionalStyle,
                  color: '#A7B6C1',
                }}
              />
            ) : (
              <Checkbox
                color="primary"
                onClick={(e) => e.stopPropagation()}
                {...toggleRowSelectedProps}
                style={{
                  ...toggleRowSelectedProps?.style,
                  ...additionalStyle,
                }}
              />
            );
          if (tooltipParams && tooltipParams.displayTooltip(row, selectedFlatRows)) {
            return (
              <Tooltip title={tooltipParams.text} placement={'right'} arrow>
                {checkbox}
              </Tooltip>
            );
          }
          return checkbox;
        },
        disableResizing: true,
        disableSortBy: true,
        width: rowWidth,
        isVisible: true,
        ...withExcelOptions<T>({
          columnName: 'Selection',
          getValue: (item: T, { selectedRowIds }) => {
            const itemId = item['id'] as string;

            return selectedRowIds[itemId] ? 'selected' : '';
          },
        }),
      },
      ...columns,
    ]);
  }
  // necessary to use the ensurePluginOrder function
  useRowSelectorReturn.pluginName = pluginName;

  return useRowSelectorReturn;
}

export default useRowSelector;
